import React, {useContext, useEffect, useMemo, useState} from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
// project imports
import Profile from './Profile';
import Localization from './Localization';
import Notification from './Notification';
import MegaMenuSection from './MegaMenuSection';

import useConfig from 'hooks/useConfig';
import { MenuOrientation, DRAWER_COLLAPSE_BREAKPOINT } from 'config';
import DrawerHeader from 'layout/Dashboard/Drawer/DrawerHeader';
import {IconButton, MenuItem, Tooltip, Typography, Menu} from '@mui/material';
import { getSubRoot } from "@/utils/utils";
import CustomSplitButton from "@/pages/custom-components/CommonComponents/CustomSplitButton";
import { useNavigate } from "react-router";
import {
  CURRENT_SPORT, defaultContentSpecificParams,
  SUPPORTED_CONTENTS, TARGET_APP_BUILD, TARGET_APP_BUILD_VALUE
} from "@/constants/globalConstants";
import { useLocation } from "react-router-dom";
import { useContentSpecificParams } from "@/contexts/ContentSpecificParamsContext";
import { useSelectedCard } from "@/contexts/SelectedCardContext";
import { useMaterializationSettings } from "@/contexts/MaterializationSettingsContext";
import { getSubRootName, sportIconMap } from '../../../../utils/utils'
import { useEditorialAssets } from "@/contexts/EditorialAssetsContext";
import {useEditState} from "@/contexts/editor/EditStateContext";
import Button from "@mui/material/Button";
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import {useRightDrawer} from "@/contexts/application/RightDrawerContext";
import {useChat} from "@/contexts/ChatContext";
import {enqueueSnackbar} from "notistack";
import {useKeyMomentActiveFilters} from "@/contexts/application/KeyMomentActiveFilters";
import {useSelectedContent} from "@/contexts/SelectedContentContext";
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import {TourContext} from "@/contexts/TourContext";
import {capitalizeEachUnderscore, toUpperWithUnderscoreSpaces} from "@/lib/utils";
import {ModeStatus} from "@/types/editorTypes";
import {useSuggestedCuts} from "@/contexts/SuggestedCutsContext";
import Collapse from "@mui/material/Collapse";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import EyeOutlined from "@ant-design/icons/EyeOutlined";
import {SimpleTreeView} from "@mui/x-tree-view/SimpleTreeView";

// ==============================|| HEADER - CONTENT ||============================== //

export default function HeaderContent() {
  const { menuOrientation } = useConfig();
  const { setContentSpecificParams } = useContentSpecificParams()
  const {setKeyMomentActiveFilters} = useKeyMomentActiveFilters()
  const { resetCardContext, setLogicClipName } = useSelectedCard();
  const { resetMaterializationSettings } = useMaterializationSettings()
  const { resetEditorialAssetSelection } = useEditorialAssets()
  const { emptyWindowLandmarks } = useEditState()
  const { toggleDrawer } = useRightDrawer()
  const { openConversation } = useChat()
  const { setSelectedEventId, setSelectedEventName, setMode } = useSelectedContent()
  const { clearSuggestedCuts } = useSuggestedCuts()

  const [openCollapseHowTo, setOpenCollapseHowTo] = useState(false);
  const [openCollapseTutorials, setOpenCollapseTutorials] = useState(true)
  const [openCollapseRelated, setOpenCollapseRelated] = useState(true)

  const tourContext = useContext(TourContext);

  const [contentType, setContentType] = React.useState(SUPPORTED_CONTENTS[CURRENT_SPORT].MATCH_HIGHLIGHT);

  // Stato per il tour con gli step da fare per l'help
  const [stepsToDo, setStepsToDo] = React.useState('');
  // Stato per la gestione dell'Ancora del Menu Help
  const [helpAnchorEl, setHelpAnchorEl] = React.useState(null);
  const isHelpMenuOpen = Boolean(helpAnchorEl);

  const navigate = useNavigate()
  
  const downLG = useMediaQuery((theme) => theme.breakpoints.down(DRAWER_COLLAPSE_BREAKPOINT));

  const localization = useMemo(() => <Localization />, []);

  const megaMenu = useMemo(() => <MegaMenuSection />, []);

  const location = useLocation();

  const getLogoFromUrl = () => {
    return sportIconMap[getSubRootName(location.pathname)];
  }

  // Modificato per gestire anche la navigazione verso la library
  const handleActionClick = async (selectedContentType = contentType) => {
    if (selectedContentType === 'GO_TO_LIBRARY') {
      setKeyMomentActiveFilters({})
      // the library of the section the user is in: clips/library from the clips pages, keymoments/library otherwise
      const section = location.pathname.split('/')[2] === 'clips' ? 'clips' : 'keymoments';
      navigate(`${getSubRoot(location.pathname)}/${section}/library`);
      setContentSpecificParams(defaultContentSpecificParams[CURRENT_SPORT][selectedContentType]);
      resetCardContext();
      resetMaterializationSettings()
      resetEditorialAssetSelection()
      return;
    }

    if (selectedContentType === 'CREATE_KEY_MOMENT') {
      setMode(ModeStatus.CREATE)
      navigate(getSubRoot(location.pathname) + '/keymoments/editor');
      setSelectedEventName("")
      setSelectedEventId(null)
      clearSuggestedCuts()
      emptyWindowLandmarks()
      setContentSpecificParams(defaultContentSpecificParams[CURRENT_SPORT][selectedContentType]);
      resetCardContext();
      resetMaterializationSettings()
      resetEditorialAssetSelection()
      return;
    }

    const basePath = `${getSubRoot(window.location.pathname)}/contentAggregation/${selectedContentType.toLowerCase()}`;
    setContentSpecificParams(defaultContentSpecificParams[CURRENT_SPORT][selectedContentType]);
    resetCardContext();
    setLogicClipName('');
    resetMaterializationSettings()
    resetEditorialAssetSelection()
    navigate(basePath)
  }

  function formatContentLabel(contentType) {
    const formattedWords = contentType
        .toLowerCase()
        .split("_")
        .map(word => {
          const cleanWord = word.trim();
          if (cleanWord === "a") return "a";
          return cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1);
        })
        .join(" ");

    return "Create a " + formattedWords;
  }

  // Apertura menu Help
  const handleHelpClick = (event) => {
    event.stopPropagation();
    setHelpAnchorEl(event.currentTarget);
  };

  // Chiusura menu Help
  const handleHelpClose = () => {
    setHelpAnchorEl(null);
  };

  // Selezione opzione ed esecuzione del Tour
  const handleSelectHelpOption = (option) => {
    handleHelpClose();
    tourContext.startTourHelp(toUpperWithUnderscoreSpaces(option));
  };

  let availableHelps = tourContext.getAvailableHelps() || [];
  React.useEffect(() => {
    availableHelps = tourContext.getAvailableHelps()
  }, [location.pathname, window.location.pathname]);

  // Prepariamo le opzioni per lo split button includendo "Go To Library" alla fine
  const splitButtonOptions = [
    ...Object.keys(SUPPORTED_CONTENTS[CURRENT_SPORT]).map(sp => ({
      id: sp,
      label: formatContentLabel(sp)
    })),
    { id: 'CREATE_KEY_MOMENT', label: 'Create a Key Moment' },
    { id: 'GO_TO_LIBRARY', label: 'Go To Library' }
  ];

  const onAIHelpClick = () => {
    try{
      toggleDrawer()
      openConversation(false)
    }catch (e){
      console.error(e)
      enqueueSnackbar("Error Starting Conversation with AI Agent");
    }
  }

  return (
      <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            px: 2, // padding orizzontale, allineato al padding del contenuto pagina (Dashboard main: p:2)
          }}
      >
        {/* Lato sinistro */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {menuOrientation === MenuOrientation.HORIZONTAL && !downLG && <DrawerHeader open={true} />}
          {/* organization with no icon / sport in sportIconMap: nothing is shown */}
          {getLogoFromUrl() && (
            <Typography
                variant="h3"
                sx={{ letterSpacing: '0.3em', display: 'flex', alignItems: 'center', gap: 3 }}
            >
              <img src={getLogoFromUrl().icon} alt="" style={{ width: 45, height: 45 }} />
              {getLogoFromUrl().sport}
            </Typography>
          )}
          {/* {!downLG && <Search />} */}
          {/* {!downLG && megaMenu} */}
          {/* {!downLG && localization} */}
        </Box>

        {/* Lato destro */}
        <Box data-tour={'quick_actions'} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {TARGET_APP_BUILD === TARGET_APP_BUILD_VALUE.BACKUP ? <></> : <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-tour={"quick_actions_box"}>
              <Typography sx={(theme) => ({ whiteSpace: 'nowrap', fontSize: theme.typography.button.fontSize, fontWeight: theme.typography.button.fontWeight })}>Quick action:</Typography>
              <CustomSplitButton
                  options={splitButtonOptions}
                  value={contentType}
                  onChange={(item)=> {
                    setContentType(item.id)
                    handleActionClick(item.id)
                  }}
                  onClickMain={({ id }) => handleActionClick(id)}
                  mainButtonProps={{ sx: { textTransform: 'none' } }}
                  menuItemProps={{ sx: { textTransform: 'none' } }}
              />
            </Box>
            <Button variant={'contained'} data-tour="ask_ai" onClick={onAIHelpClick} startIcon={<AutoAwesomeOutlinedIcon/>}>Ask AI</Button>
          </>}
          <Notification />

          {/* Dropdown Icona Help */}
          <Tooltip title={"Help"}>
            <IconButton onClick={handleHelpClick} sx={{ p: 0.5 }} >
              <HelpOutlineOutlinedIcon fontSize="medium"/>
            </IconButton>
          </Tooltip>

          <Menu
              anchorEl={helpAnchorEl}
              open={isHelpMenuOpen}
              onClose={handleHelpClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{top:'12px', left:'100px'}}
              slotProps={{
                paper: {
                  sx: {
                    maxWidth: '350px',
                    width: '100%',
                  },
                },
              }}
          >
            <Typography variant={"h4"} sx={{px:2, fontWeight: 'bold'}}>
              HELP
            </Typography>
            <Typography variant={"h6"} sx={{px: 2, fontWeight: 'bold'}}>
              Overview
            </Typography>
            <Typography sx={{px:2, marginBottom: 1}}>
              {availableHelps?.description}
            </Typography>
            <Box>
              <Box display={'flex'}>
              <Typography variant={"subtitle1"} sx={{px: 2, display: 'flex-start' }}>
                Guided Tasks
              </Typography>
                {openCollapseHowTo ? <EyeOutlined sx={{display:'flex-end'}} onClick={() => setOpenCollapseHowTo((p) => !p)}/> : <PlusOutlined sx={{display:'flex-end'}} onClick={() => setOpenCollapseHowTo((p) => !p)}/>}
              </Box>
              {openCollapseHowTo && <SimpleTreeView in={openCollapseHowTo}>
              {(availableHelps && availableHelps.helpTours && availableHelps.helpTours.length > 0) ? (


                    availableHelps.helpTours.map(option => (
                      <MenuItem
                          sx={{px:'30px'}}
                          key={option}
                          onClick={() => handleSelectHelpOption(option)}
                      >
                        <Box component={'ul'} sx={{listStyleType: 'disc'}}>
                        <Typography component={'li'} sx={{ display: 'list-item' }}>{capitalizeEachUnderscore(option)}</Typography></Box>
                      </MenuItem>
                  ))

              ) : (
                  <MenuItem sx={{px: '25px'}} disabled>No Available Helps</MenuItem>
              )}
              </SimpleTreeView>}

            </Box>
            <Box>
              <Box sx={{display:'flex'}}>
              <Typography variant={"subtitle1"} sx={{px: 2, display:'flex-start'}}>
                Tutorials
              </Typography>
                {openCollapseTutorials ? <EyeOutlined sx={{display:'flex-end'}} onClick={() => setOpenCollapseTutorials((p) => !p)}/> : <PlusOutlined sx={{display:'flex-end'}} onClick={() => setOpenCollapseTutorials((p) => !p)}/>}
              </Box>
              {openCollapseTutorials && <SimpleTreeView in={openCollapseTutorials}>
              {(availableHelps && availableHelps.helpTutorials && availableHelps.helpTutorials.length > 0) ? (

                  availableHelps.helpTutorials.map(option => (
                      <MenuItem
                          sx={{px:'30px'}}
                          key={option}
                          onClick={() => handleSelectHelpOption(option)}
                      >
                        <Box component={'ul'} sx={{listStyleType: 'disc'}}>
                          <Typography component={'li'} sx={{ display: 'list-item' }}>{capitalizeEachUnderscore(option)}</Typography></Box>
                      </MenuItem>
                    ))

              ) : (
                      <MenuItem sx={{px: '25px'}} disabled>No Available Helps</MenuItem>
                  )
              }
              </SimpleTreeView>}
            </Box>
            <Box>
              <Box sx={{display:'flex'}}>
              <Typography variant={"subtitle1"} sx={{px: 2, display:'flex-start'}} >
                Related Tours
              </Typography>
                  {openCollapseRelated ? <EyeOutlined sx={{display:'flex-end'}} onClick={() => setOpenCollapseRelated((p) => !p)}/> : <PlusOutlined sx={{display:'flex-end'}} onClick={() => setOpenCollapseRelated((p) => !p)}/>}
              </Box>
              {openCollapseRelated && <SimpleTreeView in={openCollapseRelated}>
              {(availableHelps && availableHelps.relatedTutorials && availableHelps.relatedTutorials.length > 0) ? (

                  availableHelps.relatedTutorials.map(option => (
                      <MenuItem
                          sx={{px:'30px'}}
                          key={option}
                          onClick={() => handleSelectHelpOption(option)}
                      >
                        <Box component={'ul'} sx={{listStyleType: 'disc'}}>
                          <Typography component={'li'} sx={{ display: 'list-item' }}>{capitalizeEachUnderscore(option)}</Typography></Box>
                      </MenuItem>
                  ))

              ) : (
                  <MenuItem sx={{px: '25px'}} disabled>No Available Helps</MenuItem>
              )
              }
              </SimpleTreeView>}
            </Box>
            {/*
            <MenuItem
                sx={{px:'30px'}}
                key={'mock'}
                onClick={() => { handleHelpClose(); tourContext.executeAgenticTour(['match_highlight_competition_text', 'match_highlight_match_text'], [{agenticDataType: 'text', agenticDataValue: 'Italian Serie B - 2026', isCertain: true}, {agenticDataType: 'text', agenticDataValue: 'Juv', isCertain: false}], 0)}}
            >
                <Typography>Bene</Typography>
            </MenuItem>
            <MenuItem
              sx={{px:'30px'}}
              key={'mock2'}
              onClick={() => { handleHelpClose(); tourContext.executeAgenticTour(['check_if_view_all', 'clips_view_all_open_filter_drawer_automatically', 'clips_view_all_filter_drawer_reset_button_click', 'clips_view_all_filter_drawer_global_search_overview_and_click', 'clips_view_all_filter_drawer_search_label_on_writing', 'clips_view_all_filter_drawer_competition_overview_and_click', 'clips_view_all_filter_drawer_competition_label_on_writing', 'clips_view_all_filter_drawer_competition_label_checkbox_compiling'], [{agenticDataType: '', agenticDataValue: '', isCertain: true}, {agenticDataType: '', agenticDataValue: '', isCertain:true}, {agenticDataType: '', agenticDataValue: '', isCertain:true}, {agenticDataType: '', agenticDataValue: '', isCertain: true}, {agenticDataType: 'text', agenticDataValue: 'B', isCertain: true}, {agenticDataType: '', agenticDataValue: '', isCertain: true}, {agenticDataType: 'text', agenticDataValue: 'Italian Serie B', isCertain: true}, {agenticDataType: 'button', agenticDataValue: 'Italian Serie B', isCertain: true}], 0)}}
            >
              <Typography>Benone</Typography>
            </MenuItem>
            */}
          </Menu>
          {/* <Message /> */}
          {/* {!downLG && <FullScreen />} */}
          {/* <Customization /> */}
          {/* <Setting /> */}
          <Profile />
        </Box>
      </Box>
  );
}
